import { useState, useCallback, useEffect } from 'react';
import { useDeleteCloudAccountMutation } from '../../../api';

export const useBulkAndDialogs = ({
    notifySuccess,
    notifyError,
    bulkDeleteTriggerRef,
    stopPolling,
    setDeletedApplianceIds,
    onBulkActionSuccess,
}) => {
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [bulkDeleteMode, setBulkDeleteMode] = useState(false);
    const [appliancesToDelete, setAppliancesToDelete] = useState([]);
    const [highlightedForDeleteId, setHighlightedForDeleteId] = useState(null);

    const [deleteCloudAccount, { isLoading: isDeleting }] = useDeleteCloudAccountMutation();

    const handleDeleteCancel = useCallback(() => {
        setDeleteConfirmOpen(false);
        setBulkDeleteMode(false);
        setAppliancesToDelete([]);
        setHighlightedForDeleteId(null);
    }, []);

    const handleBulkDeleteTrigger = useCallback((applianceIds) => {
        setBulkDeleteMode(true);
        setAppliancesToDelete(applianceIds);
        setDeleteConfirmOpen(true);
    }, []);

    useEffect(() => {
        if (bulkDeleteTriggerRef) {
            bulkDeleteTriggerRef.current = handleBulkDeleteTrigger;
        }
    }, [bulkDeleteTriggerRef, handleBulkDeleteTrigger]);

    const handleDeleteAppliance = useCallback(
        async (ids, setEffectiveSelectedRowKeys) => {
            const idsToDelete = bulkDeleteMode ? appliancesToDelete : Array.isArray(ids) ? ids : [ids];
            let successIds = [];
            let failedIds = [];

            for (const id of idsToDelete) {
                try {
                    // Stop polling for appliance being deleted
                    if (stopPolling) {
                        stopPolling(id);
                    }

                    await deleteCloudAccount(id).unwrap();
                    successIds.push(id);
                } catch (error) {
                    console.error(`Failed to delete appliance ${id}:`, error);
                    const errorMessage = error?.data?.message || error?.message || 'Unknown error';
                    failedIds.push({ id, error: errorMessage });
                }
            }

            if (successIds.length > 0) {
                notifySuccess({
                    description: `Successfully deleted ${successIds.length} migration appliance${successIds.length > 1 ? 's' : ''}`,
                });

                // Update local state to remove deleted appliances
                setDeletedApplianceIds((prev) => new Set([...prev, ...successIds]));
            }

            if (failedIds.length > 0) {
                const errorDetails = failedIds.map((f) => f.error).join('\n');
                notifyError({
                    description: `Failed to delete ${failedIds.length} migration appliance${failedIds.length > 1 ? 's' : ''}:\n${errorDetails}`,
                });
            }

            // Only clean up state and clear selections if ALL deletions were successful
            if (failedIds.length === 0) {
                // Clean up state completely - all deletions succeeded
                setDeleteConfirmOpen(false);
                setBulkDeleteMode(false);
                setAppliancesToDelete([]);
                setHighlightedForDeleteId(null);

                // Call the bulk action success callback to clear selections in parent
                if (onBulkActionSuccess) {
                    onBulkActionSuccess();
                }

                if (bulkDeleteMode && setEffectiveSelectedRowKeys) {
                    setEffectiveSelectedRowKeys([]);
                }
            } else {
                // Some deletions failed - only clear selections for successful ones
                if (bulkDeleteMode && setEffectiveSelectedRowKeys && successIds.length > 0) {
                    setEffectiveSelectedRowKeys((prev) => prev.filter((id) => !successIds.includes(id)));
                }

                // Update the appliances to delete list to only include failed ones
                const failedApplianceIds = failedIds.map((f) => f.id);
                setAppliancesToDelete(failedApplianceIds);

                // Keep the modal open if there are still appliances to retry
                if (failedApplianceIds.length > 0) {
                    // Don't close the modal, user can retry the failed deletions
                } else {
                    handleDeleteCancel();
                }
            }
        },
        [
            bulkDeleteMode,
            appliancesToDelete,
            deleteCloudAccount,
            notifySuccess,
            notifyError,
            stopPolling,
            setDeletedApplianceIds,
            onBulkActionSuccess,
        ],
    );

    const handleBulkDeleteConfirm = useCallback(
        async (setEffectiveSelectedRowKeys) => {
            await handleDeleteAppliance(appliancesToDelete, setEffectiveSelectedRowKeys);
        },
        [handleDeleteAppliance, appliancesToDelete],
    );

    return {
        deleteConfirmOpen,
        bulkDeleteMode,
        appliancesToDelete,
        deleteCloudAccount,
        highlightedForDeleteId,
        isDeleting: isDeleting,
        handleBulkDeleteTrigger,
        handleDeleteCancel,
        handleDeleteAppliance,
        handleBulkDeleteConfirm,
        setHighlightedForDeleteId,
    };
};
